import { InterleavedBufferAttribute, Sphere, type Vector2 } from 'three'
import type { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { createCadLineSegments2Object } from '@/plugins/cad/module/CADModule/object/material/CadLineFactory'
import type { StaticLineBatchRange } from '@/plugins/cad/module/CADModule/object/objectManager/StaticLineBatchManager'

/**
 * 对每个实体区间执行 Three.js 原生宽线拾取，先利用各自边界拒绝无关实体。
 * 仅复用 CPU attribute 视图，不创建额外 GPU buffer；命中索引转换回批次区间。
 */
export function installStaticBatchRaycast(object: LineSegments2): void {
  const geometry = new LineSegmentsGeometry()
  const sourceStart = object.geometry.getAttribute('instanceStart') as InterleavedBufferAttribute
  const sourceEnd = object.geometry.getAttribute('instanceEnd') as InterleavedBufferAttribute
  const start = new InterleavedBufferAttribute(sourceStart.data, 3, sourceStart.offset)
  const end = new InterleavedBufferAttribute(sourceEnd.data, 3, sourceEnd.offset)
  geometry.setAttribute('instanceStart', start)
  geometry.setAttribute('instanceEnd', end)
  geometry.boundingSphere = new Sphere()
  const proxy = createCadLineSegments2Object(geometry, object.material)
  object.geometry.addEventListener('dispose', () => geometry.dispose())

  object.raycast = (raycaster, intersects) => {
    proxy.matrixWorld.copy(object.matrixWorld)
    proxy.material = object.material
    // WebGPU LineSegments2 的 raycast 分辨率保存在对象自身；proxy 不参与渲染，需要从真实批次同步。
    const proxyResolution = (proxy as unknown as { _resolution?: Vector2 })._resolution
    const sourceResolution = (object as unknown as { _resolution?: Vector2 })._resolution
    proxyResolution?.copy(sourceResolution ?? proxyResolution)
    const ranges = object.userData.cadBatchEntityRanges as readonly StaticLineBatchRange[]
    for (const range of ranges) {
      start.offset = sourceStart.offset + range.segmentStart * sourceStart.data.stride
      end.offset = sourceEnd.offset + range.segmentStart * sourceEnd.data.stride
      geometry.instanceCount = range.segmentCount
      geometry.boundingBox = range.bounds
      range.bounds.getBoundingSphere(geometry.boundingSphere!)
      const firstHit = intersects.length
      proxy.raycast(raycaster, intersects)
      for (let index = firstHit; index < intersects.length; index += 1) {
        const hit = intersects[index]!
        hit.object = object
        if (hit.faceIndex != null) hit.faceIndex += range.segmentStart
      }
    }
  }
}
